import { Typography } from '@workspace/ui/components/Typography';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import type { IconTypes } from '~/components/Icon/IconTypes';
import type { PaymentMethods } from '~/types/payments';
import { formatPhone } from '~/utils/formaters';

import FooterLanguagePicker from '../FooterLanguagePicker';
import styles from './FooterCerise.module.scss';

const PAYMENT_ICONS: Partial<Record<PaymentMethods, IconTypes>> = {
  cash: 'cash',
};

const SOCIAL_LINKS: { key: string; icon: IconTypes }[] = [
  { key: 'instagram', icon: 'insta' },
  { key: 'facebook', icon: 'facebook' },
  { key: 'tiktok', icon: 'tiktok' },
];

const FooterCerise = () => {
  const { t } = useTranslation();

  const { phoneNumber, paymentMethods, appStoreUrl, links, officialName } =
    company.config;

  const socials = SOCIAL_LINKS.map((item) => ({
    ...item,
    link: links[item.key as keyof typeof links] as string | undefined,
  })).filter((item) => item.link);

  return (
    <footer className={styles.footer} data-company={company.name}>
      <div className={styles.container}>
        <div className={styles.top}>
          <div className={styles.links}>
            <div className={styles.contact}>
              <section className={styles.column}>
                <Typography.Text400Semibold
                  as="h3"
                  className={styles.columnTitle}
                >
                  {t('hotline')}
                </Typography.Text400Semibold>
                {phoneNumber && (
                  <Typography.Text300Medium as="span" className={styles.phone}>
                    {phoneNumber}
                  </Typography.Text300Medium>
                )}
              </section>

              {links.whatsapp && (
                <section className={styles.column}>
                  <Typography.Text400Semibold
                    as="h3"
                    className={styles.columnTitle}
                  >
                    {t('support')}
                  </Typography.Text400Semibold>
                  <a
                    href={links.whatsapp}
                    target="_blank"
                    rel="noreferrer"
                    data-link="footer-support-whatsapp"
                    className={styles.support}
                  >
                    <Icon type="whatsapp" size={24} />
                    <Typography.Text300Medium as="span">
                      {formatPhone(links.whatsapp)}
                    </Typography.Text300Medium>
                  </a>
                </section>
              )}
            </div>

            <section className={styles.column}>
              <Typography.Text400Semibold
                as="h3"
                className={styles.columnTitle}
              >
                {t('legal')}
              </Typography.Text400Semibold>
              <ul className={styles.list}>
                <li>
                  <Link
                    to="/legals"
                    data-link="footer-terms"
                    className={styles.link}
                  >
                    <Typography.Text200RegularHigh as="span">
                      {t('termsAndConditions')}
                    </Typography.Text200RegularHigh>
                  </Link>
                </li>
              </ul>
            </section>

            {!!paymentMethods?.length && (
              <section className={styles.column}>
                <Typography.Text400Semibold
                  as="h3"
                  className={styles.columnTitle}
                >
                  {t('ourPaymentMethods')}
                </Typography.Text400Semibold>
                <ul className={styles.list}>
                  {paymentMethods.map((method) => {
                    const icon = PAYMENT_ICONS[method];
                    if (!icon) {
                      return null;
                    }
                    return (
                      <li key={method} className={styles.payment}>
                        <Icon type={icon} size={24} />
                        <Typography.Text200RegularHigh as="span">
                          {t(method)}
                        </Typography.Text200RegularHigh>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </div>

          {(appStoreUrl.ios || appStoreUrl.android) && (
            <div className={styles.downloads}>
              <Typography.Text400Semibold
                as="h3"
                className={styles.columnTitle}
              >
                {t('downloadTheAppForYourPhone')}
              </Typography.Text400Semibold>
              <div className={styles.stores}>
                {appStoreUrl.ios && (
                  <a
                    href={appStoreUrl.ios}
                    target="_blank"
                    rel="noreferrer"
                    data-link="footer-app-store"
                    className={styles.store}
                  >
                    <Icon type="apple" size={24} className={styles.storeIcon} />
                    <span className={styles.storeText}>
                      <Typography.Text100RegularHigh as="span">
                        {t('downloadOn')}
                      </Typography.Text100RegularHigh>
                      <Typography.Text200Medium as="span">
                        App Store
                      </Typography.Text200Medium>
                    </span>
                  </a>
                )}
                {appStoreUrl.android && (
                  <a
                    href={appStoreUrl.android}
                    target="_blank"
                    rel="noreferrer"
                    data-link="footer-google-play"
                    className={styles.store}
                  >
                    <Icon type="gplay" size={24} className={styles.storeIcon} />
                    <span className={styles.storeText}>
                      <Typography.Text100RegularHigh as="span">
                        {t('getOn')}
                      </Typography.Text100RegularHigh>
                      <Typography.Text200Medium as="span">
                        Google Play
                      </Typography.Text200Medium>
                    </span>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.bottom}>
          <div className={styles.bottomStart}>
            {socials.length > 0 && (
              <div className={styles.socials}>
                {socials.map(({ key, icon, link }) => (
                  <a
                    key={key}
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    data-link={`footer-social-${key}`}
                    className={styles.social}
                  >
                    <Icon type={icon} size={20} />
                  </a>
                ))}
              </div>
            )}

            <FooterLanguagePicker />
          </div>

          <img
            src={company.images.logo.light}
            alt={officialName}
            className={styles.logo}
          />

          <Typography.Text100RegularHigh as="span" className={styles.copyright}>
            {new Date().getFullYear()} {officialName}. All rights reserved
          </Typography.Text100RegularHigh>
        </div>
      </div>
    </footer>
  );
};

export default observer(FooterCerise);
